import { useEffect, useState } from 'react'
import Edit from '@mui/icons-material/Edit'
import { Button, ButtonProps, Tooltip } from '@mui/material'
import useOverlay from '@/hooks/useOverlay'
import useIsSuperUser from '@/services/hooks/useIsSuperUser'
import CodeEditorDialog from '@/components/code-editor/CodeEditorDialog'

const FIELD_RULE_SCRIPT_TEMPLATE = `# The function name should match the function name specified in the rule configuration.
# The function should accept a single argument and return a string.
# The first argument "value" is the value of the field as it is after being put through any rules that come before this one.

def {run_rule}(value):
    return f'mod-{value}'
`

const DOCUMENT_RULE_SCRIPT_TEMPLATE = `# The function name should match the function name specified in the rule configuration.
# The first argument "needed_fields" is the value of any fields included in the "Needed Fields" configuration for this rule.

def {doc_script}(needed_fields):
    return_dict = {}
    values = []

    # Add flags for document
    # return_dict['doc_flags'] = [
    #     {'short_description': 'Doc Flag', 'level': 'error'}
    # ]

    # Add or remove tags for document
    # return_dict['doc_tags_with_operation'] = [
    #     {'name': 'Tag Name', 'operation': 'add'}
    # ]

    # Set value and flags for root fields
    # needed_fields['Subtotal']['value'] = 10.0
    # needed_fields['Subtotal']['metadata'] = {
    #     'field_metadata': 'Some metadata to save'
    # }
    # needed_fields['Subtotal']['flags'] = [
    #     {'short_description': 'Subtotal Flag', 'level': 'error'}
    # ]
    # values.append(needed_fields['Subtotal'])

    # # Set value and flags for table fields
    # needed_fields['Rate'][0]['value'] = 20.0
    # needed_fields['Rate'][0]['metadata'] = {
    #     'table_field_metadata': 'Some metadata to save'
    # }
    # needed_fields['Rate'][0]['flags'] = [
    #     {'short_description': 'Rate Flag', 'level': 'error'}
    # ]
    # values.append(needed_fields['Rate'][0])

    return_dict['values'] = values

    return return_dict
`

type RuleCodeEditorProps = {
  isFieldRule: boolean
  onSave: (value: string) => void
  isOnlyEditableBySuperUser?: boolean
  color?: ButtonProps['color']
  functionName?: string
  script?: string
}

export default function RuleCodeEditor({
  isFieldRule,
  onSave,
  isOnlyEditableBySuperUser,
  color,
  functionName,
  script,
}: RuleCodeEditorProps) {
  const codeEditorOverlay = useOverlay()
  const isSuperUser = useIsSuperUser()

  const scriptTemplate = isFieldRule
    ? FIELD_RULE_SCRIPT_TEMPLATE.replace(
        '{run_rule}',
        functionName || 'run_rule',
      )
    : DOCUMENT_RULE_SCRIPT_TEMPLATE.replace(
        '{doc_script}',
        functionName || 'doc_script',
      )
  const [value, setValue] = useState(script || scriptTemplate)

  function handleSave(value: string) {
    onSave(value)
    codeEditorOverlay.close()
  }

  useEffect(() => {
    setValue(script || scriptTemplate)
  }, [script, scriptTemplate])

  return (
    <>
      <Tooltip
        title={
          isOnlyEditableBySuperUser && !isSuperUser
            ? 'Cannot edit this script. It is managed by Pixydocs engineers.'
            : ''
        }
        enterDelay={1000}
      >
        <div>
          <Button
            disabled={isOnlyEditableBySuperUser && !isSuperUser}
            color={color}
            onClick={codeEditorOverlay.open}
            startIcon={<Edit />}
            variant="text"
            sx={{ width: 'fit-content' }}
          >
            {isOnlyEditableBySuperUser && !isSuperUser ? 'Restricted' : 'Edit'}{' '}
            Script
          </Button>
        </div>
      </Tooltip>

      <CodeEditorDialog
        title="Python Script"
        overlay={codeEditorOverlay}
        value={value}
        setValue={setValue}
        onSave={handleSave}
        onCancel={() => {
          // Reset value
          setValue(script || scriptTemplate)
        }}
      />
    </>
  )
}
